/**
 * Tool Configuration Section
 * 
 * Allows users to configure how a tool-capable node appears to AI agents
 * - Auto mode: Uses default description from node definition
 * - Manual mode: Allows custom description override
 */

import { useState, useEffect } from 'react';
import { Node, Edge } from 'reactflow';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wrench, Info } from 'lucide-react';
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import { ToolDescriptionMode } from '@shared/types/node-definition';

interface ToolConfigSectionProps {
  node: Node;
  onUpdate: (field: string, value: unknown) => void;
  nodes: Node[];
  edges: Edge[];
}

export function ToolConfigSection({ node, onUpdate, nodes, edges }: ToolConfigSectionProps) {
  const nodeDefinition = getNodeDefinition(node.type || '');
  
  // Check if this node is tool-capable
  if (!nodeDefinition?.toolCapable?.enabled) {
    return null;
  }

  const toolConfig = nodeDefinition.toolCapable;
  const allowManual = toolConfig.allowManualDescription !== false;
  
  // If manual description is not allowed, don't show any tool config UI
  if (!allowManual) {
    return null;
  }
  
  // Get current values from node data
  const descriptionMode = (node.data.toolDescriptionMode || 'auto') as 'auto' | 'manual';
  const customDescription = node.data.toolDescription || '';
  
  // Get the description that will be shown to AI
  const effectiveDescription = descriptionMode === 'manual' && customDescription 
    ? customDescription 
    : toolConfig.description;

  const handleModeChange = (mode: 'auto' | 'manual') => {
    onUpdate('toolDescriptionMode', mode);
    
    // If switching to manual and no custom description exists, pre-populate with default
    if (mode === 'manual' && !customDescription) {
      onUpdate('toolDescription', toolConfig.description);
    }
  };

  return (
    <>
      {/* Tool Description Mode */}
      <div>
        <Label htmlFor="description-mode">Node Description</Label>
        <Select value={descriptionMode} onValueChange={handleModeChange}>
          <SelectTrigger id="description-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Set Automatically</SelectItem>
            <SelectItem value="manual">Set Manually</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          {descriptionMode === 'auto' 
            ? 'Using default description from node definition'
            : 'Provide a custom description for the AI agent'}
        </p>
      </div>

      {/* Manual Description Input */}
      {descriptionMode === 'manual' && allowManual && (
        <div>
          <Label htmlFor="custom-description">Description</Label>
          <Textarea
            id="custom-description"
            value={customDescription}
            onChange={(e) => onUpdate('toolDescription', e.target.value)}
            placeholder={toolConfig.description}
            rows={4}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Describe what this tool does. This helps the AI understand when to use it.
          </p>
        </div>
      )}

      {/* Auto Description Preview */}
      {descriptionMode === 'auto' && (
        <div>
          <Label className="flex items-center gap-1">
            <Info className="h-3 w-3" />
            Default Description
          </Label>
          <div className="p-3 bg-muted border rounded-md text-sm text-muted-foreground leading-relaxed mt-1">
            {toolConfig.description}
          </div>
        </div>
      )}
    </>
  );
}
